import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type {
	EditorCommand,
	ExtractInjectionAPI,
	PMPluginFactoryParams,
} from '@atlaskit/editor-common/types';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { MentionNameDetails } from '@atlaskit/mention';

import type { AIPlugin } from '../../aiPluginType';
import type { ErrorAIEvent } from '../../editor-actions/types';
import type { EditorPluginAIProvider } from '../../types/types';
import { configItemUsesRovo } from '../../ui/components/SessionUI/utils/checkMigrationStatus';
import { createStreamFlowPacer } from '../../ui/utils/request/stream-flow-pacer';
import { createStreaming } from '../../ui/utils/streaming';
import { aiExperienceDecorationPluginKey } from '../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pure-pm-in-editor-direct-streaming/pluginKey';

import { aiSessionContextPluginKey } from './ai-session-context-plugin-key';
import {
	streamADFResponse,
	completeADFStream,
	errorADFStream,
	resetSessionState,
	updateIdMap,
	updateTraceIds,
} from './commands';
import { reducer } from './reducer';
import type { SessionContextPluginState, SessionContextAction } from './types';

export const initialState: SessionContextPluginState = {
	adfResponse: undefined,
	status: 'idle',
	responseVersion: 0,
	streamPaused: false,
	hasUserScrolled: false,
};

export interface AISessionContextPluginOptions {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	dispatch: PMPluginFactoryParams['dispatch'];
	editorPluginAIProvider: EditorPluginAIProvider;
	getIntl: PMPluginFactoryParams['getIntl'];
	getMentionNameDetails: (id: string) => Promise<MentionNameDetails | undefined>;
}

/**
 * This plugin stores the core business logic state for the AI session.
 * It manages the session lifecycle including:
 * - State transitions (idle → streaming → complete)
 * - Streaming initiation for non-orchestrator prompts
 * - Bridging stream events back to plugin state
 *
 * The view.update() method detects when streaming should start (based on decoration state)
 * and initiates the network request, bridging responses back to this plugin's state.
 * The pure-pm plugin then reacts to state changes to update the document.
 */
export const createAISessionContextPlugin = (
	options: AISessionContextPluginOptions,
): SafePlugin => {
	const { api, dispatch, editorPluginAIProvider, getIntl, getMentionNameDetails } = options;

	return new SafePlugin<SessionContextPluginState>({
		key: aiSessionContextPluginKey,
		state: {
			init: () => initialState,
			apply: (tr: ReadonlyTransaction, pluginState: SessionContextPluginState) => {
				const meta = tr.getMeta(aiSessionContextPluginKey);
				if (meta) {
					const newState = reducer({
						state: pluginState,
						action: meta as SessionContextAction,
					});
					if (newState !== pluginState) {
						dispatch(aiSessionContextPluginKey, newState);
					}
					return newState;
				}
				return pluginState;
			},
		},
		/**
		 * When all prompts are migrated to use the orchestrator, this entire view can be removed.
		 */
		view: () => {
			const streamFlowPacer = createStreamFlowPacer();
			let abortStreaming: (() => void) | undefined;
			let hasStartedStreaming = false;
			// Streaming setup is async; if cancel/reset happens before cleanup is available,
			// this "sticky" flag ensures we abort immediately once cleanup resolves.
			// It also guards against late/buffered events reactivating plugin state after cancel/reset.
			let cancelled = false;

			const cancelStreaming = () => {
				cancelled = true;
				hasStartedStreaming = false;
				if (abortStreaming) {
					abortStreaming();
					abortStreaming = undefined;
				}
			};

			return {
				update(view: EditorView, previousState: EditorState) {
					const sessionState = aiSessionContextPluginKey.getState(view.state);
					const prevSessionState = aiSessionContextPluginKey.getState(previousState);
					const decorationState = aiExperienceDecorationPluginKey.getState(view.state);
					const prevDecorationState = aiExperienceDecorationPluginKey.getState(previousState);

					// Handle cancel request - detect when cancelRequested flag was just set
					const cancelJustRequested =
						sessionState?.cancelRequested && !prevSessionState?.cancelRequested;
					if (cancelJustRequested) {
						cancelStreaming();
					}

					// Reset streaming flag and abort any in-flight request when session ends
					// This handles both explicit cancel and implicit reset
					if (sessionState?.status === 'idle') {
						cancelStreaming();
						return;
					}

					// For the fallback free-gen path: reset session state after finalize completes.
					// The 'complete' event handler defers the reset so buildCommitTransaction can
					// still read markdown/idMap from session state during the finalize flow.
					// Once finalize is done (hasFinalStream: true), it's safe to reset.
					if (sessionState?.status === 'complete' && sessionState?.userInput !== undefined) {
						const streamingState = purePmInEditorDirectStreamingPluginKey.getState(view.state);
						if (streamingState?.hasFinalStream) {
							api?.core?.actions.execute(resetSessionState());
							return;
						}
					}

					// When streamPaused changes, pause or resume the pacer so the discard screen can stop delivery
					if (sessionState?.streamPaused !== prevSessionState?.streamPaused) {
						if (sessionState?.streamPaused) {
							streamFlowPacer.pause();
						} else {
							streamFlowPacer.resume();
						}
					}

					// Detect when we should start streaming:
					// - configItem just became available (decoration state changed)
					// - Not a Rovo/orchestrator prompt
					// - Haven't already started streaming for this session
					const configItemJustSet = decorationState?.configItem && !prevDecorationState?.configItem;
					const shouldStartStream =
						configItemJustSet &&
						decorationState?.positions &&
						!hasStartedStreaming &&
						!configItemUsesRovo(decorationState.configItem?.key);

					// Detect when the fallback free-gen prompt submits user input.
					// This happens when START_FREE_GEN_STREAM action is dispatched from the PromptScreen
					// for products that don't have Rovo/onSubmit (e.g. Jira, Bitbucket).
					const freeGenStreamJustStarted =
						sessionState?.status === 'streaming' &&
						prevSessionState?.status === 'prompt' &&
						sessionState?.userInput !== undefined &&
						!hasStartedStreaming &&
						decorationState?.configItem &&
						decorationState?.positions;

					if (!shouldStartStream && !freeGenStreamJustStarted) {
						return;
					}

					if (!decorationState?.configItem || !decorationState?.positions) {
						return;
					}

					hasStartedStreaming = true;
					cancelled = false;

					// Create the streaming handler
					// Note: Analytics for streaming is handled separately via the AnalyticsFlow context
					// in the React layer. This needs fixing
					const streamingHandler = createStreaming({
						fireAIAnalyticsEvent: () => {},
						streamFlowPacer,
					});

					// Build the request context
					const positions = decorationState.positions;
					const isEmpty = positions[0] === positions[1];
					const selectionType = isEmpty ? 'empty' : 'range';
					const context = {
						aiProvider: editorPluginAIProvider,
						intl: getIntl(),
						editorView: view,
						positions,
						isEmpty,
						selectionType: selectionType as 'empty' | 'range',
						configItem: decorationState.configItem,
						userInput: freeGenStreamJustStarted ? sessionState?.userInput ?? '' : '',
						analyticsContext: { aiSessionId: '', aiGroupId: '' },
						usePageKnowledge: true,
						getMentionNameDetails,
						responseHistory: { entries: [], positionFromEnd: 0, totalSize: 0, traversals: 0 },
					};

					// Invoke streaming and bridge events to session context
					streamingHandler(context, (event) => {
						if (cancelled) {
							return;
						}
						switch (event.type) {
							case 'stream':
								if (event.adfResponse || event.markdown) {
									api?.core?.actions.execute(
										streamADFResponse({
											type: 'stream',
											adfResponse: event.adfResponse,
											markdown: event.markdown,
										}),
									);
								}
								break;
							case 'complete':
								if (event.adfResponse || event.markdown) {
									api?.core?.actions.execute(
										completeADFStream({
											type: 'complete',
											adfResponse: event.adfResponse,
											markdown: event.markdown,
										}),
									);
								}
								// NOTE: Do NOT reset session state here for the fallback free-gen path.
								// The finalize flow (handleFinalize) needs the session state (markdown/adfResponse)
								// to remain available so buildCommitTransaction can read it after
								// buildRestoreTransaction restores the original content.
								// The session will be reset later by the UI layer after accept/reject.
								// `freeGenStreamJustStarted` is captured in the closure when streaming started.
								if (!freeGenStreamJustStarted) {
									api?.core?.actions.execute(resetSessionState());
								}
								break;
							case 'response too similar':
								api?.core?.actions.execute(
									completeADFStream({
										type: 'complete',
										responseTooSimilar: true,
									}),
								);

								break;
							case 'error':
								if (event.errorInfo) {
									api?.core?.actions.execute(errorADFStream(event as ErrorAIEvent));
								}
								api?.core?.actions.execute(resetSessionState());
								break;
							case 'update traceIds':
								if (event.traceIds) {
									api?.core?.actions.execute(updateTraceIds(event.traceIds));
								}
								break;
							case 'update idMap':
								if (event.context?.idMap) {
									api?.core?.actions.execute(updateIdMap(event.context.idMap));
								}
								break;
							default:
								break;
						}
					}).then((cleanup) => {
						if (!cleanup) {
							return;
						}
						// If cancel/reset happened before cleanup was available, abort immediately.
						if (cancelled) {
							cleanup();
							abortStreaming = undefined;
							return;
						}
						abortStreaming = cleanup;
					});
				},

				destroy() {
					abortStreaming?.();
					streamFlowPacer.destroy();
				},
			};
		},
	});
};

/**
 * Gets the plugin state from an editor state.
 */
export const getAIExperienceContextPluginState = (
	state: EditorState,
): SessionContextPluginState | undefined => {
	return aiSessionContextPluginKey.getState(state);
};

export const createCommand = (action: SessionContextAction): EditorCommand => {
	return ({ tr }: { tr: Transaction }) => {
		return tr.setMeta(aiSessionContextPluginKey, action);
	};
};
